import { Link } from 'react-router-dom';
import {
  IMP_MDEP_ID,
  TEAM_ROLES,
  EXTENDED_TEAM,
  EXECUTION_PRINCIPLES,
  CONTINUOUS_PRACTICES,
  IMPLEMENTATION_PHASES,
  WAVE_REQUIREMENTS,
  DEPENDENCY_MANAGEMENT,
  SPRINT_MANAGEMENT,
  AI_DEV_GOVERNANCE,
  DEFINITION_OF_READY,
  DEFINITION_OF_DONE,
  QUALITY_GATES,
  TEAM_RESPONSIBILITIES,
  RISK_MANAGEMENT,
  REPORTING,
  SUCCESS_METRICS,
  EXECUTIVE_DASHBOARD,
  ARCHITECTURE_STAGES,
  LIVE_ROUTES,
  DEMO_TEAM,
  DEMO_PRINCIPLES,
  DEMO_PHASES,
  DEMO_DEPENDENCY_SPRINT,
  DEMO_AI_DEV,
  DEMO_DEFINITION,
  DEMO_QUALITY_GATES,
  DEMO_EXECUTIVE,
  buildMDEPDashboard,
} from '../../masterdevelopment/framework';
import {
  PMOTeamPanel,
  ExecutionPrinciplesPanel,
  ImplementationPhasesPanel,
  DependencySprintPanel,
  AIDevGovernancePanel,
  DefinitionReadyDonePanel,
  QualityGatesPanel,
  ExecutiveMDEPDashboardPanel,
} from '../../components/masterdevelopment';
import { Card, KpiCard } from '../../components/ui';

export function MasterDevelopmentPlanPage() {
  const dashboard = buildMDEPDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{IMP_MDEP_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Master Development Execution Plan</h1>
        <p className="mt-1 text-sm text-slate-500">
          Primary execution handbook — controlled implementation waves from foundation through general availability.
          Fully aligned with MPC-00001 and all governance bibles.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100 dark:border-indigo-900 dark:bg-indigo-950 dark:text-indigo-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <KpiCard label="Team Roles" value={dashboard.team_roles.toString()} />
        <KpiCard label="Principles" value={dashboard.execution_principles.toString()} />
        <KpiCard label="Phases" value={dashboard.implementation_phases.toString()} />
        <KpiCard label="Quality Gates" value={dashboard.quality_gates.toString()} />
        <KpiCard label="Governance Areas" value={dashboard.governance_areas.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Execution Architecture</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">PMO Execution Team</h2>
        <p className="mt-1 text-sm text-slate-500">{TEAM_ROLES.join(' · ')} · {EXTENDED_TEAM.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <PMOTeamPanel members={DEMO_TEAM} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Execution Principles</h2>
        <p className="mt-1 text-sm text-slate-500">{EXECUTION_PRINCIPLES.join(' · ')} · {CONTINUOUS_PRACTICES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ExecutionPrinciplesPanel items={DEMO_PRINCIPLES} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Implementation Phases (0–14)</h2>
        <p className="mt-1 text-sm text-slate-500">{WAVE_REQUIREMENTS.join(' · ')}</p>
        <p className="mt-1 text-xs text-slate-400">{IMPLEMENTATION_PHASES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ImplementationPhasesPanel items={DEMO_PHASES} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Dependency & Sprint Management</h2>
        <p className="mt-1 text-sm text-slate-500">{DEPENDENCY_MANAGEMENT.join(' · ')} · {SPRINT_MANAGEMENT.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <DependencySprintPanel items={DEMO_DEPENDENCY_SPRINT} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">AI Development Governance</h2>
        <p className="mt-1 text-sm text-slate-500">{AI_DEV_GOVERNANCE.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <AIDevGovernancePanel items={DEMO_AI_DEV} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Definition of Ready & Done</h2>
        <p className="mt-1 text-sm text-slate-500">{DEFINITION_OF_READY.join(' · ')} · {DEFINITION_OF_DONE.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <DefinitionReadyDonePanel items={DEMO_DEFINITION} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Quality Gates</h2>
        <p className="mt-1 text-sm text-slate-500">{QUALITY_GATES.join(' · ')} · {TEAM_RESPONSIBILITIES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <QualityGatesPanel items={DEMO_QUALITY_GATES} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Executive MDEP Dashboard</h2>
        <p className="mt-1 text-sm text-slate-500">
          {RISK_MANAGEMENT.join(' · ')} · {REPORTING.join(' · ')} · {SUCCESS_METRICS.join(' · ')} · {EXECUTIVE_DASHBOARD.join(' · ')}
        </p>
        <Card className="mt-4" padding="sm">
          <ExecutiveMDEPDashboardPanel items={DEMO_EXECUTIVE} />
        </Card>
      </section>
    </div>
  );
}
